import type * as C from "@zooi/contracts";
import { addDays, daysBetween, localDate } from "@zooi/domain";
import type { StatusKind } from "./ui/status-kinds.ts";
import type { WindowRow } from "./ui/measure.tsx";
import { executionStatus, type ExecutionAction } from "./action-items.ts";

// The campaigns list, its timeline and a campaign's page (docs/design-system.md
// §0.4a, plan M7 step 7i), kept apart from the components so the rules are tested.

export type CampaignTab = "active" | "closed";
export type CampaignView = "list" | "timeline";

/** Active holds running and paused campaigns; closed holds completed and stopped ones. */
export const tabOf = (campaign: Pick<C.CampaignOverview, "status">): CampaignTab =>
  campaign.status === "active" || campaign.status === "paused" ? "active" : "closed";

const PHASE: Record<C.CampaignPhase, StatusKind> = {
  not_started: "not_started", measuring: "measuring", reading_due: "due", paused: "paused", completed: "done", stopped: "stopped",
};
/** A phase as a status icon; the same in the list, the timeline and the page (design system §5.4). */
export const phaseStatus = (phase: C.CampaignPhase): StatusKind => PHASE[phase];

const ORDER: Record<C.CampaignPhase, number> = { reading_due: 0, measuring: 1, not_started: 2, paused: 3, completed: 4, stopped: 4 };

/** Readings due first, then measuring, not started and paused; closed ones by when they closed, newest first. */
export function sortCampaigns<T extends Pick<C.CampaignOverview, "phase" | "created_at" | "closed_at">>(campaigns: readonly T[]): T[] {
  return [...campaigns].sort((a, b) => ORDER[a.phase] - ORDER[b.phase]
    || (b.closed_at ?? "").localeCompare(a.closed_at ?? "")
    || b.created_at.localeCompare(a.created_at));
}

/** The person's choice of view on this device; the timeline needs a wide screen. */
export const readCampaignView = (raw: string | null): CampaignView => (raw === "timeline" ? "timeline" : "list");
/** Where this device keeps the campaigns' list or timeline (`zooi:campaigns-view`). */
export const CAMPAIGN_VIEW_KEY = "campaigns-view";

const RESULT: Record<C.ReadingValueT, StatusKind> = { positive: "positive", no_signal: "none", negative: "negative", inconclusive: "inconclusive" };
export const resultStatus = (value: C.ReadingValueT, operational = false): StatusKind =>
  operational && value === "positive" ? "accepted" : RESULT[value];

/** Each campaign as a timeline row: its window from its start, and its last confirmed reading as a dot. */
export function campaignRows(campaigns: readonly C.CampaignOverview[], zone: string, notStarted: string): WindowRow[] {
  const local = (iso: string) => localDate(Date.parse(iso), zone);
  return campaigns.map((campaign) => ({
    label: campaign.title,
    to: `/p/${campaign.product_id}/campaigns/${campaign.id}`,
    status: phaseStatus(campaign.phase),
    start: campaign.window_start ? local(campaign.window_start) : null,
    days: campaign.window_start ? campaign.window_days : null,
    mark: campaign.last_reading ? { date: local(campaign.last_reading.confirmed_at), kind: resultStatus(campaign.last_reading.value) } : null,
    note: campaign.window_start ? undefined : notStarted,
  }));
}

/** A range that holds every window and today, from the earliest start to the latest end. */
export function rangeOfRows(rows: readonly Pick<WindowRow, "start" | "days">[], today: string): { from: string; to: string } {
  const starts = rows.map((row) => row.start).filter((start): start is string => Boolean(start));
  const ends = rows.filter((row) => row.start && row.days).map((row) => addDays(row.start!, row.days!));
  const from = [today, ...starts].sort()[0];
  const to = [addDays(today, 7), ...ends].sort().at(-1)!;
  return { from, to: addDays(to, -1) };
}

/** A campaign's actions as status icons: operational work that passed reads as accepted, not as a signal. */
export function campaignActionStatus(action: ExecutionAction & Pick<C.CampaignDetail["actions"][number], "reading" | "measurement_mode">): StatusKind {
  const execution = executionStatus(action);
  if (execution) return execution;
  switch (action.status) {
    case "queued": return "approved";
    case "committed": return "running";
    case "delivered": return "delivered";
    case "live": return "observing";
    case "reading_due": return "due";
    case "evaluated": return action.reading ? resultStatus(action.reading, action.measurement_mode === "operational") : "done";
    case "failed": return "failed";
    case "expired": return "expired";
    default: return "stopped";
  }
}

const LIFECYCLE: readonly StatusKind[] = ["approved", "running", "delivered", "observing", "due", "positive", "accepted", "none", "negative", "inconclusive", "done", "failed", "expired", "stopped"];

/** A campaign's actions grouped by status, in lifecycle order. */
export function groupCampaignActions<T extends Pick<C.CampaignDetail["actions"][number], "status" | "reading" | "measurement_mode">>(actions: readonly T[]): [StatusKind, T[]][] {
  const groups = new Map<StatusKind, T[]>();
  for (const action of actions) {
    const kind = campaignActionStatus(action);
    groups.set(kind, [...(groups.get(kind) ?? []), action]);
  }
  return [...groups.entries()].sort((a, b) => LIFECYCLE.indexOf(a[0]) - LIFECYCLE.indexOf(b[0]));
}

/** The playbooks a campaign's actions follow, each once, in the order they first appear. */
export const playbooksOf = (actions: readonly Pick<C.CampaignDetail["actions"][number], "playbook_id">[]): string[] =>
  [...new Set(actions.map((action) => action.playbook_id).filter((id): id is string => Boolean(id)))];

/** How many days of the window have passed, for the progress of a measuring campaign; null before it starts. */
export function windowProgress(campaign: Pick<C.CampaignOverview, "window_start" | "window_days">, today: string, zone: string): number | null {
  if (!campaign.window_start || campaign.window_days <= 0) return null;
  const passed = daysBetween(localDate(Date.parse(campaign.window_start), zone), today);
  return Math.min(1, Math.max(0, passed / campaign.window_days));
}

/**
 * How many active campaigns work toward each root goal, counting those on
 * its supporting goals (docs/design-system.md §0.4a, goal bar). A campaign
 * whose goal is not in the tree counts for none.
 */
export function goalCampaigns(goals: readonly Pick<C.Goal, "id" | "parent_id">[], campaigns: readonly Pick<C.CampaignOverview, "goal_id" | "status">[]): Map<string, number> {
  const parent = new Map(goals.map((g) => [g.id, g.parent_id]));
  const rootOf = (id: string) => {
    let current = id;
    for (let hops = 0; hops < goals.length; hops++) {
      const up = parent.get(current);
      if (!up || !parent.has(up)) return current;
      current = up;
    }
    return current;
  };
  const counts = new Map<string, number>();
  for (const c of campaigns) {
    if (!c.goal_id || !parent.has(c.goal_id) || tabOf(c) !== "active") continue;
    const root = rootOf(c.goal_id);
    counts.set(root, (counts.get(root) ?? 0) + 1);
  }
  return counts;
}
